<script setup lang="ts">
import {
    onBeforeMount, ref, computed,
} from 'vue';
import TablePagination from '@/components/Utilities/TablePagination.vue';
import StatusBadge from '@/components/Utilities/StatusBadge.vue';
import BreadCrumbs from '@/components/Utilities/BreadCrumbs.vue';
import { FlightSchoolStatus } from '@/types/FlightSchoolStatus';
import { useUserStore } from '@/stores/user';
import router from '@/router';
import type { TypedColumns } from '@/types/QuasarOverrides';
import { useSchoolStore } from '@/stores/school';
import type FlightSchool from '@/types/FlightSchool';
import StatusFilter from '@/components/Utilities/StatusFilter.vue';
import { availableParts } from '@/types/Certificate';
import { numericSortCriteria } from '@/functions/sortCriteria';

const userStore = useUserStore();
const loading = ref(true);

type DirectoryRow = {
    id: number;
    slug: string;
    schoolName: string;
    part: string;
    status: string;
};

const columns: TypedColumns = [
    {
        name: 'id',
        label: 'id',
        field: 'id',
    },
    {
        name: 'school-name',
        label: 'School Name',
        field: 'schoolName',
        align: 'left',
        sortable: true,
    },
    {
        name: 'school-part',
        label: 'Part',
        field: 'part',
        align: 'left',
        sortable: true,
        sort: numericSortCriteria,
    },
    {
        name: 'status',
        label: 'Status',
        field: 'status',
        align: 'left',
        sortable: true,
    },
];

const rows = ref<DirectoryRow[]>([]);

const resolveSchoolStatus = (school: FlightSchool): FlightSchoolStatus => {
    if (school.isArchived) {
        return FlightSchoolStatus.Archived;
    }

    return !school.isVisible ? FlightSchoolStatus.NotVisible : FlightSchoolStatus.Active;
};

// Get parts that the school offers, sorted in descending order.
const getAllParts = (school: FlightSchool): string => {
    const parts = [...new Set(school.courses.map((course) => course.certificate.part))];

    return parts.sort((a, b) => b - a).join(', ');
};

onBeforeMount(async () => {
    const schoolStore = useSchoolStore();

    // Should also check role.
    if (userStore.user === null) {
        router.push({ name: 'login' });
        return;
    }

    if (schoolStore.schools.length === 0) {
        await schoolStore.setupForUserId(userStore.user.id);
    }

    // only push schools to list that the user is a school owner for.
    schoolStore.schools.forEach((school) => {
        if (userStore.canAdministerSchool(school.id)) {
            rows.value.push({
                id: school.id,
                slug: school.urlSlug,
                schoolName: school.name,
                part: getAllParts(school),
                status: resolveSchoolStatus(school),
            });
        }
    });

    loading.value = false;
});

const quickFilter = ref<string|null>('');
const selectedFilters = ref<string[]>([]);
const availablePartsInString = Object.values(availableParts).map((part) => part.toString());

const filterOptions = [
    { header: 'School Status', options: Object.values(FlightSchoolStatus) },
    { header: 'Part', options: availablePartsInString },
];

/**
 * Filter the available rows by status and part.
 *
 * @param availableRows - The available rows to filter.
 *
 */
const filterByStatusAndPart = (availableRows: DirectoryRow[]): DirectoryRow[] => {
    if (selectedFilters.value.length === 0) {
        return availableRows;
    }

    const statusFilter = selectedFilters.value.filter(
        (filter) => Object.values(FlightSchoolStatus).includes(filter as FlightSchoolStatus),
    );

    const partFilter = selectedFilters.value.filter(
        (filter) => availablePartsInString.includes(filter),
    );

    return availableRows.filter(
        (directoryRow: DirectoryRow): boolean => {
            const filterByStatus = statusFilter.length === 0 || statusFilter.includes(directoryRow.status);
            const filterByPart = partFilter.length === 0
                || partFilter.every((part) => directoryRow.part.split(', ').includes(part));

            return filterByStatus && filterByPart;
        },
    );
};

const filterByQuickFilter = (availableRows: DirectoryRow[]): DirectoryRow[] => {
    if (quickFilter.value === '' || quickFilter.value === null) {
        return availableRows;
    }

    const filter = quickFilter.value.toLowerCase();

    return availableRows.filter(
        (directoryRow: DirectoryRow): boolean => directoryRow.schoolName.toLowerCase().includes(filter),
    );
};

const filteredRows = computed((): DirectoryRow[] => filterByQuickFilter(filterByStatusAndPart(rows.value)));

const pagination = ref({
    page: 1,
    rowsPerPage: 15,
});
</script>
<template>
    <div class="row">
        <header class="col-12 text-center">
            <BreadCrumbs
                :bread-crumbs="[]"
                current-page="School Directory"
            />
            <h1 class="fsum-generic-header q-pb-none q-mb-xs">
                School Directory
            </h1>
        </header>
    </div>
    <div class="row">
        <div class="col-shrink q-mx-auto q-my-xl">
            <div class="row q-mx-sm">
                <div class="top-buttons col-12">
                    <div class="fit row wrap justify-end q-mb-lg">
                        <div
                            v-if="userStore.isGlobalAdmin"
                        >
                            <q-btn
                                class="fsum-button-dark-blue"
                                label="Create A School"
                                no-caps
                                :to="{ name: 'createSchool' }"
                            />
                        </div>
                    </div>
                </div>
                <div class="col-12 text-left">
                    <q-table
                        v-model:pagination="pagination"
                        :rows="filteredRows"
                        :columns="columns"
                        :visible-columns="[ 'school-name', 'school-part', 'status' ]"
                        :loading="loading"
                        row-key="name"
                        class="fsum-school-table"
                        table-header-class="bg-fsum-all-blue text-fsum-all-blue text-bold"
                        hide-pagination
                    >
                        <template #top>
                            <div class="row justify-right q-gutter-x-md q-gutter-y-md print-hide full-width">
                                <q-input
                                    v-model="quickFilter"
                                    label="Search by school name"
                                    class="bg-white"
                                    dense
                                    outlined
                                    clearable
                                >
                                    <template #append>
                                        <q-icon name="search" />
                                    </template>
                                </q-input>
                                <StatusFilter
                                    :filter-options="filterOptions"
                                    :table-loading="loading"
                                    @apply-filters="(appliedFilters) => selectedFilters = appliedFilters"
                                />
                            </div>
                        </template>
                        <template #body-cell-school-name="props">
                            <q-td>
                                <router-link
                                    class="school-info-link text-fsum-blue"
                                    :to="{
                                        name: 'schoolProfile',
                                        params: { schoolSlug: props.row.slug },
                                    }"
                                >
                                    {{ props.row.schoolName }}
                                    <q-icon
                                        name="arrow_forward_ios"
                                        right
                                    />
                                </router-link>
                            </q-td>
                        </template>
                        <template #body-cell-status="props">
                            <q-td>
                                <StatusBadge
                                    :status="props.row.status"
                                />
                            </q-td>
                        </template>
                        <template #bottom>
                            <table-pagination
                                :pagination="pagination"
                                :num-rows="filteredRows.length"
                                pagination-label="Schools per page"
                                @update:pagination="(newPagination) => pagination = newPagination"
                            />
                        </template>
                    </q-table>
                </div>
            </div>
        </div>
    </div>
</template>
<style scoped>
.school-info-link {
    text-decoration: none;
}
.selected-filter {
    border: 2px solid var(--fsum-med-green);
}
:deep(.q-table__top) {
    background-color: var(--fsum-light-blue);
}
</style>
